<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="mt-4 mb-5" style="max-width:700px">
        <h3>Synkroniser Z-rapport (Dagsoppgjør) til Tripletex</h3>

        <div v-show="!loading">
            <div v-if="locations.length === 0" class="mt-4">
              <span class="message-info">Ingen avdelinger har Tripletex aktivert. Aktiver det under Avdelinger.</span>
            </div>

            <div v-else-if="!configured" class="mt-4">
              <span class="message-info">Tripletex-konto er ikke konfigurert for denne avdelingen. Gå til <a href="/tripletex">Tripletex innstillinger</a> for å sette opp.</span>
            </div>

            <div v-if="configured">
                <div v-if="hasMultiLocations()" class="mt-4">
                  <span class="ingredient-title">Velg avdeling</span>
                  <select class="form-select" v-model="locationId" @change="onLocationChange()">
                    <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
                  </select>
                </div>

                <div class="mt-4">
                  <p class="text-muted">Synkroniser dagsoppgjør som bilag i Tripletex.</p>
                  <SelectDate ref="selectDateFrom" label="Velg fra og med dato"/>
                  <SelectDate ref="selectDateTo" label="Velg til og med dato"/>
                  <button type="button" class="bo_btn mt-2" @click="syncZReport"> Synkroniser Z-rapport </button>
                  <button type="button" class="bo_btn mt-2 ms-4" style="background-color:var(--bo-muted)" @click="validateSync"> Valider synkronisering </button>

                  <div v-if="syncCompleted" class="mt-3">
                    <div :class="syncResult.success ? 'message-success' : 'message-error'">
                      <p>{{ syncResult.message }}</p>
                      <p v-if="syncResult.successCount !== undefined">Opprettet: {{ syncResult.successCount }} av {{ syncResult.totalZReports }} | Feil: {{ syncResult.errorCount }}</p>
                    </div>
                    <div v-if="syncResult.errors" class="mt-2 message-error">
                      <span class="ingredient-title">Feildetaljer:</span>
                      <ul>
                        <li v-for="(error, index) in syncResult.errors" :key="index">{{ error }}</li>
                      </ul>
                    </div>
                    <div v-if="syncResult.existingVouchers && syncResult.existingVouchers.length > 0" class="mt-3">
                      <span class="ingredient-title">Eksisterende bilag i Tripletex:</span>
                      <table class="table table-sm table-bordered mt-1">
                        <thead>
                          <tr>
                            <th>Bilagsnr.</th>
                            <th>Dato</th>
                            <th>Beskrivelse</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(v, index) in syncResult.existingVouchers" :key="index">
                            <td>{{ v.number }}</td>
                            <td>{{ v.date }}</td>
                            <td>{{ v.description }}</td>
                            <td><button type="button" class="btn btn-sm btn-outline-danger" @click="deleteExistingVoucher(v, index)">Slett</button></td>
                          </tr>
                        </tbody>
                      </table>
                      <p class="text-muted"><strong>Merk:</strong> Tripletex tillater kun sletting av det nyeste bilaget først. Slett derfor i rekkefølge fra nyest til eldst.</p>
                    </div>
                  </div>

                  <!-- Validation results -->
                  <div v-if="validationCompleted" class="mt-4">
                    <div v-if="!validationResult.success" class="message-error">
                      <p>{{ validationResult.message }}</p>
                    </div>

                    <div v-else>
                      <h5 :class="validationResult.match ? 'text-success' : 'text-danger'">
                        {{ validationResult.match ? 'Alle beløp stemmer' : 'Avvik funnet' }}
                      </h5>
                      <p class="text-muted mb-1">Z-rapporter: {{ validationResult.zReportCount }} | Tripletex bilag: {{ validationResult.voucherCount }}</p>

                      <!-- Payment type comparison -->
                      <span class="ingredient-title">Betalingsmiddel</span>
                      <table class="table table-sm table-bordered mt-1">
                        <thead>
                          <tr>
                            <th></th>
                            <th>CAGISTA</th>
                            <th>Tripletex</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in paymentRows" :key="row.label">
                            <td :class="row.isTotal ? 'fw-bold' : ''">{{ row.label }}</td>
                            <td :class="row.isTotal ? 'fw-bold' : ''">{{ formatAmount(row.cagista) }}</td>
                            <td :class="row.isTotal ? 'fw-bold' : ''">{{ formatAmount(row.tripletex) }}</td>
                            <td>
                              <span v-if="amountsMatch(row.cagista, row.tripletex)" class="text-success fw-bold">OK</span>
                              <span v-else class="text-danger fw-bold">{{ formatAmount(row.tripletex - row.cagista) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- VAT breakdown comparison -->
                      <span class="ingredient-title mt-3">MVA-fordeling</span>
                      <table class="table table-sm table-bordered mt-1">
                        <thead>
                          <tr>
                            <th>MVA%</th>
                            <th>Brutto (CAGISTA)</th>
                            <th>Brutto (Tripletex)</th>
                            <th>MVA (CAGISTA)</th>
                            <th>MVA (Tripletex)</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in vatRows" :key="row.vatRate">
                            <td>{{ row.vatRate }}%</td>
                            <td>{{ formatAmount(row.caBrutto) }}</td>
                            <td>{{ formatAmount(row.ttBrutto) }}</td>
                            <td>{{ formatAmount(row.caVat) }}</td>
                            <td>{{ formatAmount(row.ttVat) }}</td>
                            <td>
                              <span v-if="amountsMatch(row.caBrutto, row.ttBrutto)" class="text-success fw-bold">OK</span>
                              <span v-else class="text-danger fw-bold">{{ formatAmount(row.ttBrutto - row.caBrutto) }}</span>
                            </td>
                          </tr>
                          <tr v-if="vatRows.length > 0" class="fw-bold">
                            <td>Total</td>
                            <td>{{ formatAmount(vatTotals.caBrutto) }}</td>
                            <td>{{ formatAmount(vatTotals.ttBrutto) }}</td>
                            <td>{{ formatAmount(vatTotals.caVat) }}</td>
                            <td>{{ formatAmount(vatTotals.ttVat) }}</td>
                            <td>
                              <span v-if="amountsMatch(vatTotals.caBrutto, vatTotals.ttBrutto)" class="text-success fw-bold">OK</span>
                              <span v-else class="text-danger fw-bold">{{ formatAmount(vatTotals.ttBrutto - vatTotals.caBrutto) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- Cash rounding info -->
                      <div v-if="validationResult.cashRoundingMessage" class="mt-2 alert alert-info mb-0 py-1 px-2">
                        {{ validationResult.cashRoundingMessage }}
                      </div>

                      <!-- Differences detail -->
                      <div v-if="validationResult.differences && validationResult.differences.length > 0" class="mt-2 message-error">
                        <span class="ingredient-title">Avvik:</span>
                        <ul class="mb-0" style="list-style-type: disc; padding-left: 20px;">
                          <li v-for="(diff, index) in validationResult.differences" :key="index" style="display: list-item; float: none; margin-bottom: 4px;">{{ diff }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
        </div>
      </div>
    <ConfirmDialogue ref="confirmDialogue"/>
  </div>
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import SelectDate from "@/components/other/SelectDate";
  import ConfirmDialogue from "@/components/other/ConfirmDialogue";
  import SettingsService from "@/services/settings/SettingsService";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";

  export default {
    components: {LoadingSpinner, SelectDate, ConfirmDialogue},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        configured: false,
        syncCompleted: false,
        syncResult: {},
        validationCompleted: false,
        validationResult: {}
      };
    },
    computed: {
      paymentRows() {
        if (!this.validationResult.cagista) return [];
        const ca = this.validationResult.cagista;
        const tt = this.validationResult.tripletex;
        let rows = [];
        if (ca.cash > 0 || tt.cash > 0) rows.push({ label: 'Kontant', cagista: ca.cash, tripletex: tt.cash });
        if (ca.card > 0 || tt.card > 0) rows.push({ label: 'Kort', cagista: ca.card, tripletex: tt.card });
        if (ca.bankAxept > 0 || tt.bankAxept > 0) rows.push({ label: 'BankAxept', cagista: ca.bankAxept, tripletex: tt.bankAxept });
        if (ca.vipps > 0 || tt.vipps > 0) rows.push({ label: 'Vipps', cagista: ca.vipps, tripletex: tt.vipps });
        if (ca.bonus > 0 || tt.bonus > 0) rows.push({ label: 'Bonuspoeng', cagista: ca.bonus, tripletex: tt.bonus });
        rows.push({ label: 'Total salg', cagista: ca.totalSales, tripletex: tt.totalSales, isTotal: true });
        if (ca.tips > 0 || tt.tips > 0) rows.push({ label: 'Tips', cagista: ca.tips, tripletex: tt.tips });
        return rows;
      },
      vatRows() {
        if (!this.validationResult.cagista) return [];
        const caBreakdown = this.validationResult.cagista.vatBreakdown || [];
        const ttBreakdown = this.validationResult.tripletex.vatBreakdown || [];

        // Merge VAT rates from both sources
        const rates = new Set();
        caBreakdown.forEach(v => rates.add(v.vatRate));
        ttBreakdown.forEach(v => rates.add(v.vatRate));

        return Array.from(rates).sort((a, b) => a - b).map(rate => {
          const ca = caBreakdown.find(v => v.vatRate === rate) || { brutto: 0, vat: 0, netto: 0 };
          const tt = ttBreakdown.find(v => v.vatRate === rate) || { brutto: 0, vat: 0, netto: 0 };
          return {
            vatRate: rate,
            caBrutto: ca.brutto,
            ttBrutto: tt.brutto,
            caVat: ca.vat,
            ttVat: tt.vat
          };
        });
      },
      vatTotals() {
        return this.vatRows.reduce((t, r) => ({
          caBrutto: t.caBrutto + (r.caBrutto || 0),
          ttBrutto: t.ttBrutto + (r.ttBrutto || 0),
          caVat: t.caVat + (r.caVat || 0),
          ttVat: t.ttVat + (r.ttVat || 0)
        }), { caBrutto: 0, ttBrutto: 0, caVat: 0, ttVat: 0 });
      }
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'TRIPLETEX');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.checkSyncStatus();
            } else {
              this.configured = false;
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      onLocationChange(){
        this.syncCompleted = false;
        this.validationCompleted = false;
        this.checkSyncStatus();
      },
      checkSyncStatus(){
        SettingsService.getTripletexSyncStatus(this.locationId).then(
            (response) => {
              if(response.data !== null){
                this.configured = response.data.configured;
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      syncZReport(){
        this.loading = true;
        this.syncCompleted = false;
        this.validationCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.syncZReportToTripletex(this.locationId, fromDate, toDate).then(
            (response) => {
              this.syncCompleted = true;
              this.syncResult = response.data;
              this.loading = false;
            },
            (error) => {
              this.syncCompleted = true;
              this.syncResult = { success: false, message: 'Feil ved Z-rapport synkronisering' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      async deleteExistingVoucher(voucher, index){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Slett bilag',
          message: 'Er du sikker på at du vil slette bilag ' + voucher.number + ' (' + voucher.description + ')?',
          okButton: 'Slett',
          cancelButton: 'Avbryt'
        });
        if (!ok) return;
        this.loading = true;

        SettingsService.deleteSingleVoucher(this.locationId, voucher.id).then(
            (response) => {
              if (response.data.success) {
                this.syncResult.existingVouchers.splice(index, 1);
                if (this.syncResult.existingVouchers.length === 0) {
                  this.syncCompleted = false;
                }
              } else {
                this.syncResult.message = response.data.message || 'Feil ved sletting av bilag';
              }
              this.loading = false;
            },
            (error) => {
              this.syncResult.message = 'Feil ved sletting av bilag';
              console.log(error);
              this.loading = false;
            }
        );
      },
      validateSync(){
        this.loading = true;
        this.validationCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.validateZReportSync(this.locationId, fromDate, toDate).then(
            (response) => {
              this.validationCompleted = true;
              this.validationResult = response.data;
              this.loading = false;
            },
            (error) => {
              this.validationCompleted = true;
              this.validationResult = { success: false, message: 'Feil ved validering' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      formatAmount(amount) {
        if (amount === 0 || amount === undefined) return '0,00';
        return amount.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      amountsMatch(a, b) {
        let tolerance = Math.max(0.02, (this.validationResult.zReportCount || 1) * 0.01);
        if (this.validationResult.cashRounding) {
          tolerance = Math.max(tolerance, 1.0);
        }
        return Math.abs((a || 0) - (b || 0)) <= tolerance;
      }
    }
  };
</script>
